import { useState } from "react";
import { DateFormat, EnvKey, RequestStatus, RequestType, Rules, SystemCode } from "../../../../../../constants";
import { DateHelper, EnvHelper, StringHelper } from "../../../../../../helper";
import { IViagsLeaveInfo } from "../../../../../../services/viags/application-for-leave.service";
import { useHistory, useLocation } from "react-router-dom";
import { Col, DatePicker, Form, Input, Row, Typography } from "antd";
import { useSearchViagsUser } from "../../hooks/use-search-viags-user";
import { useAFLSteps, useCreateAFL, useUpdateAFL } from "../../hooks";
import { useViagsUpload } from "../../hooks/use-viags-upload";
import { UserHelper } from "../../utils";
import { AttachFileButton, Box, Button, MicrosoftPeopleSelect, ViagsUpload } from "../../../../../../components";
import BoxSectionItem from "../../../../../../components/box-section/box-section-item";
import { FormText } from "../../../application-for-leave/components";
import omit from "lodash/omit";
import { AFLType } from "../../models";
import moment from "moment";
import locale from "antd/es/date-picker/locale/vi_VN";
import "moment/locale/vi";
import { aflTypeShortLabel } from "../../constants";
interface Props {
  data?: any;
}

export const CreateApplicationForTerminationContractForm = ({ data = {} }: Props) => {
  const history = useHistory();
  const [form] = Form.useForm();
  const { pathname } = useLocation();
  const [searchedUser, setSearchedUser] = useState({} as IViagsLeaveInfo);
  const { searchViagsUser, isLoading: isSearching } = useSearchViagsUser();
  const { createApplicationForLeave } = useCreateAFL();
  const { updateApplicationForLeave } = useUpdateAFL();
  const { mapUploadResponse } = useViagsUpload();
  const { getAvailableStep, notifyAnother, getSuspendContractSteps } = useAFLSteps();

  const siteUrl = EnvHelper.getValue(
    pathname.includes("tsn")
      ? EnvKey.APPROVER_AFL_TERMINATION_CONTRACT_TSN_URL
      : pathname.includes("dad")
      ? EnvKey.APPROVER_AFL_TERMINATION_CONTRACT_DAD_URL
      : EnvKey.APPROVER_AFL_URL,
  );

  const isCreateSession = !data?.id;

  const eTitle = Form.useWatch("eTitle", form);
  const ePaidLeaveRemain = Form.useWatch("ePaidLeaveRemain", form);
  const eVacationPeriods = Form.useWatch("eVacationPeriods", form);
  const isValidDates = isCreateSession ? !!ePaidLeaveRemain : !!data?.ePaidLeaveRemain;

  const daysOffNum =
    eVacationPeriods && !eVacationPeriods.some((i: any) => Boolean(!i))
      ? DateHelper.duration(eVacationPeriods!).days()
      : 0;

  const [titleCode] = StringHelper.verticalBarSplit(eTitle ?? StringHelper.verticalBarSplit(data?.eTitle)[0]);

  const isDAD = ["DAD", "VDAD"].includes(searchedUser?.listDepartment?.[0]?.code);
  const isTSN = ["TSN", "VTSN"].includes(searchedUser?.listDepartment?.[0]?.code);

  const isLocationTSN = pathname.includes("tsn")
    ? "/procedure/termination-contract-tsn"
    : "/procedure/termination-contract-dad";

  const onSearch = (employeeId: string) => {
    searchViagsUser(
      { employeeId },
      {
        onSuccess(response) {
          const [userInfo] = response;

          setSearchedUser(userInfo);
          form.setFieldsValue({
            ...UserHelper.getDepartmentInfo(userInfo),
            eRequestName: userInfo.fullName,
            eEmployeeBirthday: moment(userInfo.birthDay),
            ePaidLeaveRemain: userInfo.paidLeave_Remain_Early,
            ePhone: userInfo.mobile_1 || userInfo.mobile_2,
            eTitle: `${userInfo.code_Job_Title}|${userInfo.jobTitle_NameVN}`,
            eAddress: userInfo.permanent,
          });
        },
      },
    );
  };

  const onSubmit = (values: any) => {
    const { initialize } = getAvailableStep(SystemCode.AFL, values.eApprovedBy);
    values["eAttachments"] = mapUploadResponse(values.eAttachments);

    const departmentCode = searchedUser.listDepartment[0].code;
    const eBranch = `${departmentCode}|${UserHelper.getBranchTitle(departmentCode!)}`;

    if (!isDAD && !isTSN) return;

    values["eSteps"] = getSuspendContractSteps({
      branchCode: departmentCode,
      dayOffNumber: daysOffNum,
      initialize,
      titleCode,
      unitCode: searchedUser.listDepartment[1].code,
      eApproverBy: values.eApprovedBy,
      eTeamLead: values.eTeamLead,
      isOverseas: values.isOverseas,
    });

    values["eApprovalProcess"] = { current: 0, status: "finish" };

    const body = {
      ...omit(values, ["eTeamLead", "eDirector"]),
      isOverseas: !!values.isOverseas,
      Title: "Đơn xin chấm dứt hợp đồng lao động",
      eAFLType: `${AFLType.TerminationContractApplication}|Đơn xin chấm dứt hợp đồng lao động`,
      eRequestStatus: RequestStatus.submitted,
      eRequestType: RequestType.confirm,
      eBranch,
    };

    createApplicationForLeave(body, {
      onSuccess(response: any) {
        const { id, eRequestStatus, eRequestName, eEmail } = response.fields;
        history.push(`${isLocationTSN}/${response.id}`);
        notifyAnother({
          steps: JSON.parse(response.fields.eSteps),
          toPath: `${siteUrl}/${id}`,
          eRequestStatus,
          current: JSON.parse(response.fields.eApprovalProcess).current,
          requestUser: { name: eRequestName, email: eEmail },
          reason: "",
          aflTypeLabel: aflTypeShortLabel[AFLType.TerminationContractApplication],
        });
      },
    });
  };

  const onEdit = (values: any) => {
    values["eAttachments"] = mapUploadResponse(values.eAttachments);
    updateApplicationForLeave(
      { id: data!.id, body: values },
      {
        successText: "Chỉnh sửa yêu cầu thành công.",
        errorText: "Chỉnh sửa yêu cầu thất bại.",
        onSuccess(response: any) {
          history.push(`${isLocationTSN}/${response.id}`);
        },
      },
    );
  };

  return (
    <Form
      form={form}
      onFinish={isCreateSession ? onSubmit : onEdit}
      size="large"
      layout="vertical"
      initialValues={{
        eVacationPeriods: [null],
        eAttachments: [],
        ...data,
      }}
    >
      <Box title="Thông tin">
        <Row gutter={[24, 0]}>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Mã nhân viên" name="eEmployeeId" rules={[Rules.required()]}>
                <Input.Search loading={isSearching} onSearch={onSearch} />
              </Form.Item>
            ) : (
              <BoxSectionItem ellipsis textLeft="Mã nhân viên" textRight={data?.eEmployeeId} />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Họ tên" name="eRequestName" rules={[Rules.required()]}>
                <Input disabled />
              </Form.Item>
            ) : (
              <BoxSectionItem ellipsis textLeft="Họ tên" textRight={data?.eRequestName} />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Ngày sinh" name="eEmployeeBirthday" rules={[Rules.required()]}>
                <DatePicker disabled className="w-full" format={DateFormat.DD_MM_YYY} placeholder="" />
              </Form.Item>
            ) : (
              <BoxSectionItem
                ellipsis
                textLeft="Ngày sinh"
                textRight={DateHelper.formatDateToString(data?.eEmployeeBirthday)}
              />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item
                label={
                  <Typography.Text ellipsis={{ tooltip: "Số ngày phép còn lại trong năm" }}>
                    Số ngày phép còn lại trong năm
                  </Typography.Text>
                }
                name="ePaidLeaveRemain"
              >
                <Input disabled />
              </Form.Item>
            ) : (
              <BoxSectionItem ellipsis textLeft="Số ngày phép còn lại trong năm" textRight={data?.ePaidLeaveRemain} />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Chức danh" name="eTitle">
                <FormText />
              </Form.Item>
            ) : (
              <BoxSectionItem
                ellipsis
                textLeft="Chức danh"
                textRight={StringHelper.verticalBarSplit(data?.eTitle)[1]}
              />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Cơ quan/Đơn vị" name="eDepartment">
                <FormText />
              </Form.Item>
            ) : (
              <BoxSectionItem
                ellipsis
                textLeft="Cơ quan/Đơn vị"
                textRight={StringHelper.verticalBarSplit(data?.eDepartment)[1]}
              />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Đơn vị" name="eUnit">
                <FormText />
              </Form.Item>
            ) : (
              <BoxSectionItem ellipsis textLeft="Đơn vị" textRight={StringHelper.verticalBarSplit(data?.eUnit)[1]} />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Đội/Nhóm" name="eTeam">
                <FormText />
              </Form.Item>
            ) : (
              <BoxSectionItem ellipsis textLeft="Đội/Nhóm" textRight={StringHelper.verticalBarSplit(data?.eTeam)[1]} />
            )}
          </Col>
        </Row>
      </Box>
      <Box title="Thông tin chi tiết">
        <Row gutter={[16, 0]}>
          <Col span={24}>
            <Form.Item name="eAttachments" valuePropName="fileList" getValueFromEvent={(e) => e.fileList}>
              <ViagsUpload>
                <AttachFileButton />
              </ViagsUpload>
            </Form.Item>
          </Col>
          <Col span={12} xs={24} md={12}>
            <Form.Item
              label="Thời gian chấm dứt hợp đồng lao động"
              name="eTerminationContractTime"
              rules={[
                {
                  required: true,
                  message: "Vui lòng chọn trường này!",
                },
              ]}
            >
              <DatePicker
                className="min-w-full"
                placeholder="Chọn thời gian nghỉ"
                format={DateFormat.DD_MM_YYYY}
                locale={locale}
              />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} md={12}>
            <Form.Item
              label="Địa chỉ thường trú"
              name="eAddress"
              rules={[
                {
                  required: true,
                  message: "Vui lòng nhập trường này!",
                },
              ]}
            >
              <Input disabled={!isValidDates} />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} md={12}>
            <Form.Item label="Nơi nghỉ" name="eLeavePlace" rules={[Rules.required("Vui lòng nhập trường này!")]}>
              <Input disabled={!isValidDates} />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} md={12}>
            <Form.Item label="Thời gian công tác tại cơ quan, chi nhánh" name="eBusinessTime">
              <Input disabled={!isValidDates} />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} md={12}>
            <Form.Item label="Code Sabre" name="eCodeSabre">
              <Input disabled={!isValidDates} />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} md={12}>
            <Form.Item label="Số lượng vật tư trả lại" name="eAssetReturnNum">
              <Input disabled={!isValidDates} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Lý do"
              name="eReason"
              rules={[
                {
                  required: true,
                  message: "Vui lòng nhập trường này!",
                },
              ]}
            >
              <Input.TextArea disabled={!isValidDates} />
            </Form.Item>
          </Col>
        </Row>
        {isCreateSession && (
          <>
            <Form.Item
              label="Chọn trưởng bộ phận"
              name="eApprovedBy"
              rules={[Rules.required("Vui lòng chọn trường này")]}
            >
              <MicrosoftPeopleSelect disabled={!isValidDates} />
            </Form.Item>
          </>
        )}
      </Box>
      <div className="gap-10 flex-end">
        <Button disabled={!isValidDates} size="lg" style={{ minWidth: 120 }} variant="danger" onClick={history.goBack}>
          Huỷ
        </Button>
        <Button disabled={!isValidDates} size="lg" type="submit" style={{ minWidth: 120 }} variant="confirm">
          Xác nhận
        </Button>
      </div>
    </Form>
  );
};
